import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Paper,
  Card,
  CardContent,
  Divider,
  Alert
} from '@mui/material';
import { Save, Preview } from '@mui/icons-material';
import api from '../services/api';

const CardCreator = () => {
  const [searchParams] = useSearchParams();
  const templateParam = searchParams.get('template') || '1';
  const [templateId, setTemplateId] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  const navigate = useNavigate();

  // Fetch template ID from database
  const fetchTemplateId = async () => {
    try {
      setLoadingTemplate(true);
      
      // If templateParam is a number, use it directly
      if (!isNaN(templateParam) && Number.isInteger(Number(templateParam))) {
        setTemplateId(parseInt(templateParam));
        return;
      }
      
      // If templateParam is a string (like "template2"), fetch from database
      const response = await fetch(`https://api.webcard.digital/api/templates`);
      const data = await response.json();
      
      if (data.templates && Array.isArray(data.templates)) {
        // Find template by name or slug
        const foundTemplate = data.templates.find(t => 
          t.name === templateParam || 
          t.slug === templateParam || 
          t.id.toString() === templateParam
        );
        
        if (foundTemplate) {
          setTemplateId(foundTemplate.id);
        } else {
          console.error('Template not found:', templateParam);
          setTemplateId(1); // Default fallback
        }
      } else {
        setTemplateId(1); // Default fallback
      }
    } catch (error) {
      console.error('Error fetching template ID:', error);
      setTemplateId(1); // Default fallback
    } finally {
      setLoadingTemplate(false);
    }
  };

  // Fetch template ID on component mount
  useEffect(() => {
    fetchTemplateId();
  }, [templateParam]);
  
  const [template, setTemplate] = useState(null);
  const [cardData, setCardData] = useState({
    personalInfo: {
      firstName: '',
      lastName: '',
      title: '',
      company: '',
      email: '',
      phone: '',
      website: ''
    },
    socialMedia: {
      linkedin: '',
      twitter: '',
      instagram: '',
      facebook: ''
    },
    additionalInfo: {
      bio: '',
      address: '',
      customFields: []
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (templateId) {
      fetchTemplate();
    } else {
      navigate('/cards/templates');
    }
  }, [templateId]);

  const fetchTemplate = async () => {
    try {
      const response = await api.get(`/templates/${templateId}`);
      setTemplate(response.data.template);
    } catch (error) {
      console.error('Error fetching template:', error);
      navigate('/cards/templates');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    setCardData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const response = await api.post('/cards/create', {
        templateId: templateId, // Already parsed as integer
        cardData
      });
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving card:', error);
      const apiMsg = error.response?.data?.message;
      setSaveError(
        typeof apiMsg === 'string' && apiMsg.trim()
          ? apiMsg.trim()
          : 'Failed to save. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Container>Loading...</Container>;
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Create Your Card
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Template: {template.name}
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Personal Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={cardData.personalInfo.firstName}
                  onChange={(e) => handleInputChange('personalInfo', 'firstName', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={cardData.personalInfo.lastName}
                  onChange={(e) => handleInputChange('personalInfo', 'lastName', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Job Title"
                  value={cardData.personalInfo.title}
                  onChange={(e) => handleInputChange('personalInfo', 'title', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company"
                  value={cardData.personalInfo.company}
                  onChange={(e) => handleInputChange('personalInfo', 'company', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={cardData.personalInfo.email}
                  onChange={(e) => handleInputChange('personalInfo', 'email', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={cardData.personalInfo.phone}
                  onChange={(e) => handleInputChange('personalInfo', 'phone', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Website"
                  value={cardData.personalInfo.website}
                  onChange={(e) => handleInputChange('personalInfo', 'website', e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Social Media
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="LinkedIn"
                  value={cardData.socialMedia.linkedin}
                  onChange={(e) => handleInputChange('socialMedia', 'linkedin', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Twitter"
                  value={cardData.socialMedia.twitter}
                  onChange={(e) => handleInputChange('socialMedia', 'twitter', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Instagram"
                  value={cardData.socialMedia.instagram}
                  onChange={(e) => handleInputChange('socialMedia', 'instagram', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Facebook"
                  value={cardData.socialMedia.facebook}
                  onChange={(e) => handleInputChange('socialMedia', 'facebook', e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Additional Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Bio"
                  multiline
                  rows={3}
                  value={cardData.additionalInfo.bio}
                  onChange={(e) => handleInputChange('additionalInfo', 'bio', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={cardData.additionalInfo.address}
                  onChange={(e) => handleInputChange('additionalInfo', 'address', e.target.value)}
                />
              </Grid>
            </Grid>

            {saveError ? (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSaveError('')}>
                {saveError}
              </Alert>
            ) : null}
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Card'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Preview />}
                onClick={() => {/* Preview functionality */}}
              >
                Preview
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Preview
              </Typography>
              <Box sx={{ 
                border: '1px dashed #ccc', 
                p: 2, 
                textAlign: 'center',
                minHeight: 200,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <Typography variant="body2" color="text.secondary">
                  Card preview will appear here
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CardCreator;



